import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
// Hero photo by Lloyd Alozie via Pexels (free to use).
import hero from '../assets/images/dubai-skyline-dusk.jpg';
import villa from '../assets/images/villa.jpg';
import apartment from '../assets/images/apartment.jpg';
import waterfront from '../assets/images/burj-al-arab.jpg';
import downtown from '../assets/images/downtown.jpg';
import interiorBg from '../assets/images/interior.jpg';

const collections = [
  { title: 'Villas', note: 'Private residences with space to live', image: villa },
  { title: 'Apartments', note: 'Considered homes in prime towers', image: apartment },
  { title: 'Waterfront', note: 'Palm, Marina and beachfront living', image: waterfront },
  { title: 'Off-plan', note: 'New-build access, ahead of launch', image: downtown },
];

const services = [
  ['Buying', 'Search, shortlisting, viewings and negotiation for end-users and investors purchasing in Dubai.'],
  ['Selling', 'Pricing strategy, presentation and a qualified-buyer network to move your property with discretion.'],
  ['Leasing', "Tenant sourcing, screening and renewal management for landlords across Dubai's key communities."],
  ['Off-plan & investment', 'Developer access, payment-plan analysis and yield forecasting for new-build acquisitions.'],
];

const steps = [
  ['Listen', 'A private consultation to understand your goals, budget and timeline.'],
  ['Curate', 'A short, verified shortlist — never a flood of listings.'],
  ['Negotiate', 'Straight advice and firm representation through to agreement.'],
  ['Complete', 'One contact through transfer, Ejari, DEWA and handover.'],
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-ink text-paper">
        <img src={hero} alt="Aerial view of the Burj Khalifa, Dubai skyline and Sheikh Zayed Road interchange at dusk" className="absolute inset-0 h-full w-full animate-kenburns object-cover md:object-[center_24%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/10" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />

        <div className="wrap relative pb-40 pt-36">
          <p className="eyebrow animate-fade-up text-brass-light">Dubai real estate</p>
          <span className="rule-light mt-5 animate-fade-up [animation-delay:100ms]" />
          <h1 className="mt-8 max-w-[13ch] animate-fade-up text-[clamp(2.9rem,7vw,6rem)] leading-[1.02] [animation-delay:200ms]">
            A curated collection of Dubai's <em className="font-light italic text-brass-light">finest</em> addresses.
          </h1>
          <p className="mt-8 max-w-[46ch] animate-fade-up text-[1.1rem] text-paper/85 [animation-delay:400ms]">
            Address Collective represents a select portfolio of homes and investments across Dubai — chosen, verified, and presented with the care of a firm that treats every address as a long-term relationship.
          </p>
          <div className="mt-10 flex animate-fade-up flex-wrap gap-4 [animation-delay:600ms]">
            <Link to="/contact#enquiry-form" className="btn btn-gold">Book a consultation</Link>
            <a href="#services" className="btn btn-outline-light">What we handle</a>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="bg-sand/45 py-24 md:py-32">
        <div className="wrap">
          <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <h2 className="display max-w-[14ch]">Find your kind of address.</h2>
            <p className="max-w-[34ch] text-[0.98rem] text-ink/75">Four ways into the Dubai market — each with its own advisor knowledge.</p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {collections.map((c, i) => (
              <Reveal key={c.title} delay={i * 100}>
                <Link to="/contact" className="group relative block aspect-[3/4] overflow-hidden rounded-sm bg-ink">
                  <img src={c.image} alt={c.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent opacity-90" />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-paper">
                    <span className="rule-light mb-4 w-8 transition-all duration-500 group-hover:w-20" />
                    <h3 className="text-[1.7rem]">{c.title}</h3>
                    <p className="mt-1 text-sm text-paper/80">{c.note}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-ink py-24 text-paper md:py-36">
        <div className="wrap grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24">
          <Reveal className="lg:sticky lg:top-32 lg:self-start">
            <p className="eyebrow text-brass-light">What we handle</p>
            <span className="rule-light mt-5" />
            <h2 className="display mt-8 max-w-[12ch]">Four disciplines, one point of contact.</h2>
            <Link to="/contact" className="btn btn-gold mt-10">Start a conversation</Link>
          </Reveal>
          <div>
            {services.map(([h, p], i) => (
              <Reveal key={h} delay={i * 80}>
                <div className="group grid grid-cols-[auto_1fr] gap-x-7 border-t border-paper/20 py-9 transition-colors hover:border-brass-light">
                  <span className="font-serif text-xl font-light text-brass-light">0{i + 1}</span>
                  <div>
                    <h3 className="text-[1.7rem] transition-transform duration-500 group-hover:translate-x-2">{h}</h3>
                    <p className="mt-3 max-w-[48ch] text-[0.98rem] text-sand/85">{p}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-paper/20" />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-t border-ink/15 pb-24 pt-24 md:pb-32">
        <div className="wrap">
          <Reveal className="mb-16">
            <h2 className="display max-w-[14ch]">From first call to front door.</h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-4 md:gap-6">
            {steps.map(([h, p], i) => (
              <Reveal key={h} delay={i * 120}>
                <div className="relative border-t border-brass pt-6">
                  <span className="absolute -top-[5px] left-0 h-2.5 w-2.5 rounded-full bg-brass" />
                  <span className="font-serif text-sm text-ink/60">Step 0{i + 1}</span>
                  <h3 className="mb-3 mt-2 text-[1.6rem]">{h}</h3>
                  <p className="text-[0.95rem] text-ink/75">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-ink py-28 text-paper md:py-40">
        <img src={interiorBg} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <Reveal className="wrap relative">
          <span className="rule-light" />
          <h2 className="display mt-8 max-w-[16ch]">Thinking about buying, selling or leasing?</h2>
          <p className="mt-6 max-w-[40ch] text-[1.08rem] text-sand">Tell us what you need and we'll get back to you within 1–3 business days.</p>
          <Link to="/contact#enquiry-form" className="btn btn-gold mt-10">Book a consultation</Link>
        </Reveal>
      </section>
    </>
  );
}
